import IconAnswerCorrect from '@features/common/components/IconAnswerCorrect';
import IconAwardCup from '@features/common/components/IconAwardCup';
import IconClock from '@features/common/components/IconClock';
import CommonTooltip from '@features/common/components/CommonTooltip';
import { getCdnUrl } from '@features/common/store/common.selectors';
import { ITournamentParticipantRanking } from '@features/gameplay/models/tournament-participant-ranking.model';
import Image from 'next/image';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import InformationIconButton from '@features/common/components/InformationIconButton';

interface IRankingGridRowProps {
	isSelected: boolean;
	position: number;
	showAwards: boolean;
	participant: Omit<ITournamentParticipantRanking, 'id' | 'tournamentParticipantId'>;
	type: 'CURRENT_GAME_RANKING' | 'LEADERBOARD' | 'CHAMPIONSHIPS';
}

interface IRankingGridParticipantProps extends IRankingGridRowProps {
	className: string;
	children: React.ReactNode;
}

const RankingGridRowParticipantAward = (
	props: Pick<IRankingGridParticipantProps, 'participant' | 'type' | 'showAwards' | 'className'>
) => {
	const { participant, type, showAwards, className } = props;
	const { awardName, status } = participant;
	const { formatMessage } = useIntl();

	const getCorrectTextForTooltip = () => {
		if (status === 'DISQUALIFIED') {
			return 'bcs-Tournament-Ranking-Participant-Status-Description-Disqualified';
		}

		if (status === 'DOES_NOT_MEET_CONDITIONS') {
			return 'bcs-Tournament-Ranking-Participant-Status-Description-Not-Meet-Conditions';
		}

		return 'bcs-Gameplay-Award';
	};

	if (!showAwards) {
		return null;
	}

	return (
		<div className={className || ''}>
			<CommonTooltip
				type={awardName ? 'max-content' : 'contained'}
				className={`flex items-center justify-start w-full ml-4 s:w-[85%] s:pl-2`}
				text={formatMessage({ id: getCorrectTextForTooltip() })}
			>
				{awardName ? (
					<>
						<IconAwardCup width={'14'} height={'15'} />
						<p
							data-award-name={awardName}
							className={classNames('flex-1 pt-1 truncate ml-[0.3125rem]', {
								'text-white': type === 'CURRENT_GAME_RANKING',
								'text-dark-600': type !== 'CURRENT_GAME_RANKING',
							})}
						>
							{awardName}
						</p>
					</>
				) : null}
				{status === 'DISQUALIFIED' || status === 'DOES_NOT_MEET_CONDITIONS' ? (
					<>
						{status === 'DISQUALIFIED' ? (
							<svg
								xmlns='http://www.w3.org/2000/svg'
								width='13'
								height='12'
								viewBox='0 0 13 12'
								fill='none'
							>
								<path
									d='M12.1719 1.20857L10.9633 0L6.17188 4.79143L1.38045 0L0.171875 1.20857L4.9633 6L0.171875 10.7914L1.38045 12L6.17188 7.20857L10.9633 12L12.1719 10.7914L7.38045 6L12.1719 1.20857Z'
									fill='#DF4047'
								/>
							</svg>
						) : (
							<IconAwardCup width={'14'} height={'15'} />
						)}
						<p
							className={classNames(
								'overflow-hidden overflow-ellipsis whitespace-nowrap mr-2.5 ml-[0.3125rem]',
								{
									'text-razzmatazz-700': status === 'DISQUALIFIED',
									'text-dark-600': status === 'DOES_NOT_MEET_CONDITIONS',
								}
							)}
						>
							{status === 'DOES_NOT_MEET_CONDITIONS'
								? '-'
								: formatMessage({ id: 'bcs-Tournament-Ranking-Participant-Status' })}
						</p>
						<span className='min-h-3 min-w-3'>
							<InformationIconButton width={12} height={12} color='#808191' />
						</span>
					</>
				) : null}
			</CommonTooltip>
		</div>
	);
};

const RankingGridRowParticipant = (
	props: Omit<IRankingGridParticipantProps, 'position' | 'isSelected' | 'showAwards'>
) => {
	const { participant, className, type, children } = props;
	const { correctAnswersCount, numOfCorrectAnswers, averageAnswerTime } = participant;
	const { formatMessage, formatNumber } = useIntl();

	const getCorrectPoints = () => {
		if (type === 'CHAMPIONSHIPS') {
			return correctAnswersCount;
		}
		return numOfCorrectAnswers;
	};

	const formatAverageTime = (averageAnswerTime: number) => {
		return formatNumber(averageAnswerTime, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	};

	return (
		<div className={`flex items-center pr-4 text-xs md:text-sm mt-[5px] ${className}`}>
			<CommonTooltip
				type='max-content'
				className='flex items-center justify-start ml-4'
				text={formatMessage({ id: 'bcs-Gameplay-Correct' })}
			>
				<IconAnswerCorrect />
				<p
					className={classNames('flex-1 pl-1 overflow-hidden overflow-ellipsis whitespace-nowrap', {
						'text-white': type === 'CURRENT_GAME_RANKING',
						'text-dark-600': type !== 'CURRENT_GAME_RANKING',
					})}
				>
					{getCorrectPoints()}
				</p>
			</CommonTooltip>
			<CommonTooltip
				type='max-content'
				className='s:w-[13%] w-full flex items-center justify-start pl-2'
				text={formatMessage({
					id: 'bcs-Gameplay-AverageAnswerTime',
				})}
			>
				<span className='text-darkYellow-400'>
					<IconClock view={'TOURNAMENT_VIEW'} diameter={'14'} />
				</span>
				<p
					className={classNames('flex-1 pl-1 overflow-hidden overflow-ellipsis whitespace-nowrap', {
						'text-white': type === 'CURRENT_GAME_RANKING',
						'text-dark-600': type !== 'CURRENT_GAME_RANKING',
					})}
				>
					{formatAverageTime(averageAnswerTime)}
				</p>
			</CommonTooltip>
			{children}
		</div>
	);
};

const GameplayRankingGridRow = ({ isSelected, position, participant, type, showAwards }: IRankingGridRowProps) => {
	const avatarSize = '30px';

	const cdnUrl = useSelector(getCdnUrl);
	const { formatNumber, formatMessage } = useIntl();

	const getRowStyle = () => {
		switch (type) {
			case 'CURRENT_GAME_RANKING':
				if (isSelected) {
					return 'lg:pb-2 bg-yellow-400/50';
				}
				if (position % 2 === 0) {
					return 'lg:pb-2 bg-grayBlue-200/5';
				}
				return 'lg:pb-2 ';
			case 'LEADERBOARD':
			case 'CHAMPIONSHIPS':
				if (position % 2 === 0) {
					return 'pb-2 bg-grayBlue-200';
				}
				return 'pb-2 ';
		}
	};

	const getCorrectAvatarId = () => {
		if (type === 'CHAMPIONSHIPS') {
			return participant.avatarId;
		}
		return participant.customerAvatarId;
	};

	const getCorrectPoints = () => {
		if (type === 'CHAMPIONSHIPS') {
			return participant.correctAnswersCount;
		}
		return participant.numOfCorrectAnswers;
	};

	const formatAverageTime = (averageAnswerTime: number) => {
		return formatNumber(averageAnswerTime, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	};

	return (
		<div
			data-nickname={participant.customerNickname}
			className={classNames(
				`flex flex-col s:h-[4.875rem] h-[5.9375rem] lg:h-[3.25rem] pt-2 px-4 lg:px-5 text-left font-bold w-full ${getRowStyle()}`,
				{
					'md:rounded-lg': type === 'CURRENT_GAME_RANKING',
					'lg:rounded-lg': type !== 'CURRENT_GAME_RANKING',
				}
			)}
		>
			<div className='pb-1 overflow-hidden flex' key={participant.customerNickname}>
				<div
					className={classNames('w-3/4 flex items-center', {
						'lg:w-5/12': showAwards,
						'lg:w-1/2': !showAwards,
						'text-white': type === 'CURRENT_GAME_RANKING',
						'text-dark-600': type !== 'CURRENT_GAME_RANKING',
					})}
				>
					<span className='min-w-[15%] md:min-w-[11%] sm:min-w-[13%] lg:min-w-[10.5%] xl:min-w-[9%] 2xl:min-w-[5%] 2xl:w-[3.875rem]'>
						{position}
					</span>
					<div className='flex flex-col justify-center items-center'>
						<div
							className='mt-1 rounded-full w-[1.875rem] h-[1.875rem]'
							style={{
								boxShadow: 'rgba(0, 0, 0, 0.25) 0px 2px 3px',
							}}
						>
							<Image
								alt='avatar'
								unoptimized={true}
								src={`${cdnUrl}avatars/${getCorrectAvatarId()}.jpg`}
								className='rounded-full h-full'
								priority={true}
								width={avatarSize}
								height={avatarSize}
							/>
						</div>
					</div>
					<p className='w-3/5 ml-2 md:ml-4 mr-2 lg:mr-0 overflow-hidden overflow-ellipsis whitespace-nowrap'>
						{participant.customerNickname}
					</p>
				</div>
				<div
					className={classNames('w-1/4 flex justify-evenly', {
						'lg:w-7/12 lg:pl-0 pl-2.5': showAwards,
						'lg:w-1/2': !showAwards,
					})}
				>
					<p
						className={classNames('hidden lg:flex items-center w-1/3', {
							'text-grayText-500': type === 'CURRENT_GAME_RANKING',
							'text-grayBlue-700': type === 'LEADERBOARD' || type === 'CHAMPIONSHIPS',
						})}
						data-testid='numOfCorrectAnswers'
					>
						{getCorrectPoints()}
					</p>
					<p
						className={classNames('hidden lg:flex items-center w-1/3', {
							'pl-2.5': type === 'CHAMPIONSHIPS' && !showAwards,
							'text-grayText-500': type === 'CURRENT_GAME_RANKING',
							'text-grayBlue-700': type === 'LEADERBOARD' || type === 'CHAMPIONSHIPS',
						})}
						data-testid='averageAnswerTime'
					>
						{formatAverageTime(participant.averageAnswerTime)}
					</p>
					<p
						className={classNames('flex items-center w-full lg:w-1/3', {
							'text-yellow-400': type === 'CURRENT_GAME_RANKING',
							'text-gold-500': type === 'CHAMPIONSHIPS' || type === 'LEADERBOARD',
							'lg:pl-4 pl-2.5': type === 'CHAMPIONSHIPS' && !showAwards,
						})}
						data-testid='totalScore'
					>
						{type === 'CHAMPIONSHIPS'
							? formatNumber(participant.score ? participant.score : 0, {
									useGrouping: true,
							  })
							: formatNumber(participant.totalScore ? participant.totalScore : 0, {
									useGrouping: true,
							  })}
					</p>
					<div
						className={classNames('hidden w-2/6 lg:w-full lg:min-w-[25%] items-center', {
							'lg:hidden': !showAwards,
							'lg:flex': showAwards,
							'text-white': type === 'CURRENT_GAME_RANKING',
							'text-dark-600':
								(type === 'CHAMPIONSHIPS' || type === 'LEADERBOARD') &&
								participant.status !== 'DISQUALIFIED',
							'text-razzmatazz-700':
								(type === 'CHAMPIONSHIPS' || type === 'LEADERBOARD') &&
								participant.status === 'DISQUALIFIED',
						})}
						style={{ fontFamily: 'Roboto' }}
					>
						{participant.awardName ? (
							<p
								className='ml-2 lg:ml-0 overflow-hidden overflow-ellipsis whitespace-nowrap'
								title={participant.awardName}
								data-award-name={participant.awardName}
							>
								{participant.awardName}
							</p>
						) : null}
						{participant.status === 'DISQUALIFIED' || participant.status === 'DOES_NOT_MEET_CONDITIONS' ? (
							<div>
								<CommonTooltip
									className='flex items-center justify-start'
									type='contained'
									pageView='Other'
									text={formatMessage({
										id:
											participant.status === 'DISQUALIFIED'
												? 'bcs-Tournament-Ranking-Participant-Status-Description-Disqualified'
												: 'bcs-Tournament-Ranking-Participant-Status-Description-Not-Meet-Conditions',
									})}
								>
									{participant.status === 'DISQUALIFIED' ? (
										<svg
											xmlns='http://www.w3.org/2000/svg'
											width='14'
											height='14'
											viewBox='0 0 14 14'
											fill='none'
										>
											<path
												d='M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z'
												fill='#DF4047'
											/>
										</svg>
									) : null}
									<p
										className={classNames(
											'overflow-hidden overflow-ellipsis whitespace-nowrap mr-2.5',
											{
												'ml-2.5': participant.status === 'DISQUALIFIED',
											}
										)}
									>
										{participant.status === 'DOES_NOT_MEET_CONDITIONS'
											? '-'
											: formatMessage({ id: 'bcs-Tournament-Ranking-Participant-Status' })}
									</p>
									<span className='min-h-3 min-w-3'>
										<InformationIconButton width={12} height={12} color='#808191' />
									</span>
								</CommonTooltip>
							</div>
						) : null}
					</div>
				</div>
			</div>
			<RankingGridRowParticipant className='lg:hidden' type={type} participant={participant}>
				<RankingGridRowParticipantAward
					className='hidden s:flex w-full'
					showAwards={showAwards}
					type={type}
					participant={participant}
				/>
			</RankingGridRowParticipant>
			<RankingGridRowParticipantAward
				className='s:hidden flex items-center pr-4 text-xs md:text-sm mt-[0.3125rem]'
				showAwards={showAwards}
				type={type}
				participant={participant}
			/>
		</div>
	);
};

export default GameplayRankingGridRow;
